import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetContactQuery,
  useUpdateContactMutation,
  useAddContactsToListMutation,
  useRemoveContactFromListMutation,
  useBulkAddTagMutation,
  useBulkRemoveTagMutation,
} from "@/store/rest_api";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ContactForm, { ContactFormData } from "@/components/pages/contacts/ContactForm";

export default function EditContactPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: contactResp, isLoading, error } = useGetContactQuery(id!, { skip: !id });
  const contact = (contactResp as any)?.data ?? contactResp; // supports {data: Contact} or Contact

  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
  const [addToList] = useAddContactsToListMutation();
  const [removeFromList] = useRemoveContactFromListMutation();
  const [bulkAddTag] = useBulkAddTagMutation();
  const [bulkRemoveTag] = useBulkRemoveTagMutation();

  const handleSubmit = async (data: ContactFormData) => {
    if (!id) return;

    try {
      // 1) update base fields
      await updateContact({ id, data }).unwrap();

      // 2) sync list membership by diff (reuse existing endpoints)
      const existingListIds =
          contact?.contactListLinks
              ?.map((l: any) => l?.contactList?.id)
              .filter(Boolean) ?? [];

      const nextListIds = (data.listIds ?? []).filter(Boolean);

      const toAdd = nextListIds.filter((x) => !existingListIds.includes(x));
      const toRemove = existingListIds.filter((x: string) => !nextListIds.includes(x));

      if (toAdd.length > 0) {
        await Promise.all(
            toAdd.map((listId: string) =>
                addToList({ listId, contactIds: [id] }).unwrap()
            )
        );
      }

      if (toRemove.length > 0) {
        await Promise.all(
            toRemove.map((listId: string) =>
                removeFromList({ listId, contactId: id }).unwrap()
            )
        );
      }

      // 3) sync tag membership by diff
      const existingTagIds =
          contact?.contactTags
              ?.map((ct: any) => ct?.tag?.id)
              .filter(Boolean) ?? [];

      const nextTagIds = (data.tagIds ?? []).filter(Boolean);

      const tagsToAdd = nextTagIds.filter((x) => !existingTagIds.includes(x));
      const tagsToRemove = existingTagIds.filter((x: string) => !nextTagIds.includes(x));

      if (tagsToAdd.length > 0) {
        await Promise.all(
            tagsToAdd.map((tagId: string) =>
                bulkAddTag({ tagId, contactIds: [id] }).unwrap()
            )
        );
      }

      if (tagsToRemove.length > 0) {
        await Promise.all(
            tagsToRemove.map((tagId: string) =>
                bulkRemoveTag({ tagId, contactIds: [id] }).unwrap()
            )
        );
      }

      toast.success("Contact updated");
      navigate(`/contacts/${id}/detail`);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to update contact");
    }
  };

  if (!id) {
    navigate("/contacts");
    return null;
  }

  if (isLoading) {
    return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );
  }

  if (error || !contact) {
    return (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <p className="text-sm text-destructive">Failed to load contact</p>
          <Button variant="ghost" onClick={() => navigate("/contacts")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Contacts
          </Button>
        </div>
    );
  }

  return (
      <div className="space-y-6 p-4 max-w-3xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edit Contact</h1>
          <p className="text-muted-foreground">
            {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "Contact"}
          </p>
        </div>

        <ContactForm
            initialData={contact}
            onSubmit={handleSubmit}
            isSubmitting={isUpdating}
            submitLabel="Save Changes"
        />
      </div>
  );
}